import React, { lazy, Suspense, useContext, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  Navigate,
} from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import { ToastContext, ToastProvider } from "./utils/ToastContext";
import { AmeyoProvider } from "./utils/AmeyoContext";
import Loader from "./Components/Loader";
import ToastComponent from "./Components/ToastComponent";
import ProtectedRoute from "./utils/ProtectedRoute";
import { useNavigate } from "react-router-dom";
import { SidebarProvider } from "./utils/SidebarContext";
import Layout from "./Layout/layout";
import AuthCheckRoute from "./utils/AuthCheckRoute";
import MFACheckRoute from "./utils/MFACheckRoute";
import Dashboard from "./Screens/Dashboard";
import CommunicationTemplate from "./Screens/CommunicationTemplate";
import LeadDistributionRatio from "./Screens/LeadDistributionRatio";

const MFAScreen = lazy(() => import("./Screens/MFAScreen"));
// const Dashboard = lazy(() => import("./Screens/Dashboard"));
const ManageLeads = lazy(() => import("./Screens/ManageLeads"));
const LeadPreview = lazy(() => import("./Screens/LeadPreview"));
const ManageUsers = lazy(() => import("./Screens/ManageUsers"));
const Requesthistory = lazy(() => import("./Screens/Requesthistory"));
const SmartView = lazy(() => import("./Screens/SmartView"));
const NotFound = lazy(() => import("./Screens/NotFound"));
const UserPermission = lazy(() => import("./Screens/UserPermission"));
const ForgotPassScreen = lazy(() => import("./Screens/ForgotPassScreen"));
const ResetPassScreen = lazy(() => import("./Screens/ResetPassScreen"));
const DeleteLeads = lazy(() => import("./Screens/DeleteLeads"))
const TemplateConfigScreen = lazy(() =>
  import("./Screens/TemplateConfigScreen")
);
const ProspectSourceMapping = lazy(() =>
  import("./Screens/ProspectSourceMapping")
);

const TokenRefreshFailureHandler = () => {
  const { showToast } = useContext(ToastContext);

  const navigate = useNavigate();

  useEffect(() => {
    const handleTokenRefreshFailed = () => {
      navigate("/");
      showToast("Session has expired", "warning");
    };

    window.addEventListener("token-refresh-failed", handleTokenRefreshFailed);

    return () => {
      window.removeEventListener(
        "token-refresh-failed",
        handleTokenRefreshFailed
      );
    };
  }, [navigate]);

  return null;
};

const PageTransition = ({ children }) => {
  const pageVariants = {
    initial: {
      opacity: 0,
      x: -20,
    },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        ease: "easeInOut",
      },
    },
    exit: {
      opacity: 0,
      x: 20,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      className="w-full"
    >
      {children}
    </motion.div>
  );
};

// AnimatedRoutes component to handle route transitions
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Layout />}>
          {/* Redirect base URL to /login */}
          <Route
            index
            element={<Navigate to="/login" replace />}
          />
          <Route path="/login" element={<AuthCheckRoute />} />
          <Route
            path="/mfa"
            element={
              <MFACheckRoute>
                <PageTransition>
                  <MFAScreen />
                </PageTransition>
              </MFACheckRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PageTransition>
                <ForgotPassScreen />
              </PageTransition>
            }
          />
          <Route
            path="/reset-password/"
            element={
              <PageTransition>
                <ResetPassScreen />
              </PageTransition>
            }
          />
          <Route path="/" element={<AuthCheckRoute />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <Dashboard />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manageLeads"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <ManageLeads />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leadPreview/:leadsid"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <LeadPreview />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manageUsers"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <ManageUsers />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/requesthistory"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <Requesthistory />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/smartview/:tabname?"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <SmartView />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/deleteLeads"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <DeleteLeads />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-permissions"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <UserPermission />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wtemplates"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <TemplateConfigScreen />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/psmautomation"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <ProspectSourceMapping />
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* communication templates */}
          <Route
            path="/comm-template"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <CommunicationTemplate/>
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* Distribution Ratio */}
          <Route
            path="/lead-distribution-ratio"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <LeadDistributionRatio/>
                </PageTransition>
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <NotFound />
                </PageTransition>
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </AnimatePresence>
  );
};

const App = () => {
  return (
    <Router>
      <Suspense fallback={<Loader />}>
        <SidebarProvider>
          <ToastProvider>
            <AmeyoProvider>
              <ToastComponent />
              <TokenRefreshFailureHandler />
              <AnimatedRoutes />
            </AmeyoProvider>
          </ToastProvider>
        </SidebarProvider>
      </Suspense>
    </Router>
  );
};

export default App;
